import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { analyticsApi, polygonsApi } from '../lib/api';
import {
    Card, KpiTile, TrendChart, BreakdownDonut, BreakdownBars, RiskScatter,
    Leaderboard, PolygonTable,
} from './analytics/components';
import RiskMap from './analytics/RiskMap';
import {
    mockSummary, mockTimeseries, mockBreakdown, mockLeaderboard, mockPolygons, squaresFromRows,
} from './analytics/mockData';
import type {
    AnalyticsFilters, SummaryResponse, TimeseriesResponse, BreakdownResponse,
    LeaderboardResponse, PolygonsResponse,
} from './analytics/types';
import { TS_METRICS, PERIODS } from './analytics/types';

const COMMODITIES = ['cocoa', 'coffee', 'palm_oil', 'rubber', 'soy', 'timber', 'cattle'];

async function safe<T>(p: Promise<T>, fallback: T): Promise<T> {
    try { return await p; } catch { return fallback; }
}

export default function AnalyticsPage() {
    const { t } = useTranslation();
    const isMock = new URLSearchParams(useLocation().search).get('mock') === '1';

    const [filters, setFilters] = useState<AnalyticsFilters>({ period: '90d' });
    const [trendMetric, setTrendMetric] = useState<string>('area_deforested');
    const [breakdownDim, setBreakdownDim] = useState<string>('commodity');

    const [summary, setSummary] = useState<SummaryResponse | null>(null);
    const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
    const [breakdown, setBreakdown] = useState<BreakdownResponse | null>(null);
    const [compliance, setCompliance] = useState<BreakdownResponse | null>(null);
    const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
    const [polygons, setPolygons] = useState<PolygonsResponse | null>(null);
    const [geojson, setGeojson] = useState<{ type: string; features: unknown[] } | null>(null);
    const [loading, setLoading] = useState(true);

    const params = useMemo(() => {
        const p: Record<string, string> = { period: filters.period };
        if (filters.commodity) p.commodity = filters.commodity;
        if (filters.country) p.country = filters.country;
        if (filters.status) p.status = filters.status;
        if (filters.risk) p.risk = filters.risk;
        return p;
    }, [filters]);

    // primary load (filters)
    useEffect(() => {
        let alive = true;
        setLoading(true);
        (async () => {
            if (isMock) {
                if (!alive) return;
                const mp = mockPolygons();
                setSummary(mockSummary());
                setBreakdown(mockBreakdown(breakdownDim));
                setCompliance(mockBreakdown('status'));
                setLeaderboard(mockLeaderboard('deforestation_pct'));
                setPolygons(mp);
                setGeojson(squaresFromRows(mp.items));
                setLoading(false);
                return;
            }
            const [s, b, comp, l, pg] = await Promise.all([
                safe(analyticsApi.summary(params), null as SummaryResponse | null),
                safe(analyticsApi.breakdown(breakdownDim, params), null as BreakdownResponse | null),
                safe(analyticsApi.breakdown('status', params), null as BreakdownResponse | null),
                safe(analyticsApi.leaderboard('deforestation_pct', params), null as LeaderboardResponse | null),
                safe(analyticsApi.polygons(params), null as PolygonsResponse | null),
            ]);
            if (!alive) return;
            setSummary(s); setBreakdown(b); setCompliance(comp); setLeaderboard(l); setPolygons(pg);
            setLoading(false);
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, isMock]);

    // Lot geometries for the risk map — fetched once; the map renders only the
    // polygons whose id is in the (filter-respecting) analytics rows, colored by risk.
    useEffect(() => {
        if (isMock) return; // mock geojson is derived in the primary load
        let alive = true;
        (async () => {
            const gj = await safe(polygonsApi.getGeoJSON(), null as { type: string; features: unknown[] } | null);
            if (alive) setGeojson(gj);
        })();
        return () => { alive = false; };
    }, [isMock]);

    // breakdown dimension switch
    useEffect(() => {
        let alive = true;
        (async () => {
            const b = isMock ? mockBreakdown(breakdownDim) : await safe(analyticsApi.breakdown(breakdownDim, params), null as BreakdownResponse | null);
            if (alive) setBreakdown(b);
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakdownDim]);

    // trend metric switch
    useEffect(() => {
        let alive = true;
        (async () => {
            const ts = isMock ? mockTimeseries(trendMetric) : await safe(analyticsApi.timeseries(trendMetric, params), null as TimeseriesResponse | null);
            if (alive) setTimeseries(ts);
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trendMetric, params, isMock]);

    const countries = useMemo(
        () => Array.from(new Set((polygons?.items || []).map((x) => x.country).filter(Boolean))) as string[],
        [polygons]
    );

    const select = "text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5";

    return (
        <div className="space-y-5">
            {/* Header + filters */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-white">{t('analytics.title')}</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('analytics.subtitle')}</p>
                    </div>
                    {isMock && <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">mock data</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        {PERIODS.map((p) => (
                            <button key={p.key} onClick={() => setFilters((f) => ({ ...f, period: p.key }))}
                                className={`px-3 py-1.5 text-sm ${filters.period === p.key ? 'bg-primary-700 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <select className={select} value={filters.commodity || ''} onChange={(e) => setFilters((f) => ({ ...f, commodity: e.target.value || undefined }))}>
                        <option value="">{t('analytics.allCommodities')}</option>
                        {COMMODITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select className={select} value={filters.country || ''} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value || undefined }))}>
                        <option value="">{t('analytics.allCountries')}</option>
                        {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select className={select} value={filters.status || ''} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}>
                        <option value="">{t('analytics.allStatus')}</option>
                        <option value="compliant">compliant</option>
                        <option value="non_compliant">non_compliant</option>
                        <option value="pending">pending</option>
                    </select>
                </div>
            </div>

            {loading && !summary ? (
                <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
                <>
                    {/* KPI row */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                        {(summary?.kpis || []).map((k) => <KpiTile key={k.key} kpi={k} />)}
                    </div>

                    {/* Trend (2/3) + compliance donut (1/3) */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <Card className="lg:col-span-2" title={t('analytics.trend')}
                            action={
                                <select className={select} value={trendMetric} onChange={(e) => setTrendMetric(e.target.value)}>
                                    {TS_METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                                </select>
                            }>
                            <TrendChart points={timeseries?.points || []} />
                        </Card>
                        <Card title={t('analytics.byCompliance')}>
                            <BreakdownDonut groups={compliance?.groups || []} />
                            <p className="mt-2 text-xs text-gray-500 text-center">{t('analytics.donutHint')}</p>
                        </Card>
                    </div>

                    {/* Map + leaderboard */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <Card className="lg:col-span-2" title={t('analytics.riskMap')}>
                            <RiskMap geojson={geojson} riskRows={polygons?.items || []} />
                        </Card>
                        <Card title={t('analytics.topRisk')}>
                            <Leaderboard items={leaderboard?.items || []} />
                        </Card>
                    </div>

                    {/* Breakdown bars + scatter */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <Card title={t('analytics.breakdown')}
                            action={
                                <select className={select} value={breakdownDim} onChange={(e) => setBreakdownDim(e.target.value)}>
                                    <option value="commodity">commodity</option>
                                    <option value="country">country</option>
                                    <option value="status">status</option>
                                    <option value="risk">risk</option>
                                </select>
                            }>
                            <BreakdownBars groups={breakdown?.groups || []} valueKey="count" />
                        </Card>
                        <Card title={t('analytics.scatter')}>
                            <RiskScatter items={polygons?.items || []} />
                        </Card>
                    </div>

                    {/* Table */}
                    <Card title={t('analytics.lots')}>
                        <PolygonTable rows={polygons?.items || []} />
                    </Card>
                </>
            )}
        </div>
    );
}
